"use client";

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FullDisclaimerContent() {
  const [activeSection, setActiveSection] = useState("");
  const sectionsRef = useRef<{
    [key: string]: HTMLElement | null;
  }>({});
  const sections = [{
    id: "legal-disclaimer",
    title: "Legal Disclaimer"
  }, {
    id: "intellectual-property",
    title: "Intellectual Property & Copyright"
  }, {
    id: "regulatory-body",
    title: "Regulatory Body"
  }, {
    id: "contact-information",
    title: "Contact Information"
  }];
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      for (const section of sections) {
        const element = sectionsRef.current[section.id];
        if (element) {
          const {
            offsetTop,
            offsetHeight
          } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const scrollToSection = (id: string) => {
    const element = sectionsRef.current[id];
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  return <section id="full-disclaimer-content" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 max-w-7xl">
        <div ref={headerRef} className={`mb-12 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h1 className="text-foreground mb-4">Disclaimer</h1>
          <p className="text-muted-foreground text-lg">
            Important information regarding the use of this website and our
            services
          </p>
        </div>

        <div className="flex gap-12 lg:gap-16">
          {}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24">
              <nav className="space-y-1">
                {sections.map((section, i) => <div key={section.id} className="relative" data-index={i}>
                    <Button variant="ghost" onClick={() => scrollToSection(section.id)} className={`w-full justify-start text-left px-4 py-3 transition-all duration-200 ${activeSection === section.id ? "text-primary font-semibold bg-primary-light" : "text-muted-foreground hover:text-foreground hover:bg-surface"}`}>
                      {section.title}
                    </Button>
                    {activeSection === section.id && <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary" />}
                  </div>)}
              </nav>
            </div>
          </aside>

          {}
          <div className="flex-1 max-w-3xl">
            <div className="space-y-12">
              {}
              <div ref={el => {
              sectionsRef.current["legal-disclaimer"] = el;
            }}>
                <h2 className="text-foreground mb-6">Legal Disclaimer</h2>
                <div className="space-y-6 leading-relaxed">
                  <p className="text-foreground">
                    The information contained on this website is for general
                    information purposes only. While we endeavor to keep the
                    information up to date and correct, we make no
                    representations or warranties of any kind, express or
                    implied, about the completeness, accuracy, reliability,
                    suitability, or availability with respect to the website or
                    the information, products, services, or related graphics
                    contained on the website for any purpose.
                  </p>
                  <p className="text-foreground">
                    Any reliance you place on such information is therefore
                    strictly at your own risk. In no event will we be liable
                    for any loss or damage including without limitation,
                    indirect or consequential loss or damage, or any loss or
                    damage whatsoever arising from loss of data or profits
                    arising out of, or in connection with, the use of this
                    website.
                  </p>
                  <p className="text-foreground">
                    Through this website, you may be able to link to other
                    websites which are not under our control. We have no control
                    over the nature, content, and availability of those sites.
                    The inclusion of any links does not necessarily imply a
                    recommendation or endorse the views expressed within them.
                  </p>
                  <p className="text-foreground">
                    Every effort is made to keep the website up and running
                    smoothly. However, we take no responsibility for, and will
                    not be liable for, the website being temporarily unavailable
                    due to technical issues beyond our control.
                  </p>
                </div>
              </div>

              <Separator className="my-8 opacity-50" />

              {}
              <div ref={el => {
              sectionsRef.current["intellectual-property"] = el;
            }}>
                <h2 className="text-foreground mb-6">
                  Intellectual Property & Copyright
                </h2>
                <div className="space-y-6 leading-relaxed">
                  <p className="text-foreground">
                    Unless otherwise stated, we own the intellectual property
                    rights for all material on this website. All intellectual
                    property rights are reserved. You may access this from the
                    website for your own personal use subjected to restrictions
                    set in these terms and conditions.
                  </p>
                  <p className="text-foreground font-semibold">
                    You must not:
                  </p>
                  <ul className="list-disc list-inside space-y-3 text-foreground ml-4">
                    <li>Republish material from this website</li>
                    <li>Sell, rent, or sub-license material from the website</li>
                    <li>
                      Reproduce, duplicate, or copy material from this website
                    </li>
                    <li>Redistribute content from this website</li>
                  </ul>
                  <p className="text-foreground">
                    This agreement shall begin on the date hereof. Parts of this
                    website offer an opportunity for users to post and exchange
                    opinions and information in certain areas of the website. We
                    do not filter, edit, publish, or review comments prior to
                    their presence on the website. Comments do not reflect our
                    views and opinions or those of our agents and/or affiliates.
                  </p>
                  <p className="text-foreground">
                    We reserve the right to monitor all comments and to remove
                    any comments which can be considered inappropriate,
                    offensive, or causes breach of these Terms and Conditions.
                  </p>
                </div>
              </div>

              <Separator className="my-8 opacity-50" />

              {}
              <div ref={el => {
              sectionsRef.current["regulatory-body"] = el;
            }}>
                <h2 className="text-foreground mb-6">Regulatory Body</h2>
                <div className="space-y-6 leading-relaxed">
                  <p className="text-foreground">This firm is regulated by the Association of Chartered Certified Accountants (ACCA). We are registered comply with all regulatory requirements set forth by our governing body.</p>
                  <div className="card p-8 mt-8">
                    <div className="flex items-center gap-6">
                      <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1542563/img/acca_logo.png" alt="ACCA Logo" className="h-16 object-contain" />
                      <div>
                        <p className="text-foreground font-semibold mb-2">ACCA Registered&nbsp;</p>
                        <p className="text-muted-foreground text-sm">
                          Association of Chartered Certified Accountants
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-foreground mt-6">Company Registration: Sloane &amp; Co. LLP, registered in England and Wales with company number OC314505.</p>
                </div>
              </div>

              <Separator className="my-8 opacity-50" />

              {}
              <div ref={el => {
              sectionsRef.current["contact-information"] = el;
            }}>
                <h2 className="text-foreground mb-6">Contact Information</h2>
                <div className="space-y-6 leading-relaxed">
                  <p className="text-foreground">
                    If you have any questions about this disclaimer or our
                    services, please contact us using the information below:
                  </p>
                  <div className="card p-8 space-y-4">
                    <div>
                      <p className="text-foreground font-semibold mb-2">
                        Address
                      </p>
                      <p className="text-muted-foreground">Office 015, 30 Great Guildford Street, Borough, London, SE1 0HS.</p>
                    </div>
                    <div>
                      <p className="text-foreground font-semibold mb-2">
                        Phone
                      </p>
                      <p className="text-muted-foreground">020 7221 3292</p>
                    </div>
                    <div>
                      <p className="text-foreground font-semibold mb-2">
                        Business Hours
                      </p>
                      <p className="text-muted-foreground">Monday - Friday: 9:30 AM - 5:30 PM<br />Saturday - Sunday: Closed</p>
                    </div>
                  </div>

                  <div className="mt-8">
                    
                    <div className="flex flex-wrap items-center gap-8">
                      
                      
                      
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}